import { useState } from 'react'
import type { OperationalEventFormValues } from '@klear/operational/wire.client'
import {
  operationalEventFormToCreateWire,
  operationalEventFormToUpdateWire,
  validateOperationalEventForm
} from '@klear/operational/wire.client'
import { trpc, type RouterInputs } from '@klear/ui/lib/trpc'
import { OperationalForm } from '@klear/order-event-ui/event-forms/operational-form'
import { EventFormShell, type EventFormHandlerProps } from '@klear/order-event-ui/event-shared/event-form-shell'
import { useEventFormState } from '@klear/order-event-ui/event-shared/event-form-state'
import { useEventFormSave, type RunEventCommit } from '@klear/order-event-ui/event-shared/use-event-form-save'

export function OperationalEventForm({ bootstrap, orderId, traderId, onSuccess, onCancel }: EventFormHandlerProps) {
  const formState = useEventFormState<OperationalEventFormValues>({
    initial: bootstrap.formValues as OperationalEventFormValues,
    validate: validateOperationalEventForm
  })
  const [isLoading, setIsLoading] = useState(false)
  const { save } = useEventFormSave()
  const commit = trpc.operational.commit.useMutation()
  const parentEventId = bootstrap.parentEventId

  const handleSubmit = async (data: OperationalEventFormValues) => {
    setIsLoading(true)
    try {
      const runCommit: RunEventCommit = ({ id }) => {
        const input = (
          id == null
            ? { traderId, data: operationalEventFormToCreateWire(data, orderId, parentEventId) }
            : { id, traderId, data: operationalEventFormToUpdateWire(data, parentEventId) }
        ) as RouterInputs['operational']['commit']
        return commit.mutateAsync(input)
      }
      await save({
        traderId,
        eventId: bootstrap.editEventId,
        keptDocumentIds: (data.documents ?? []).map((document) => document.id),
        originalDocumentIds: bootstrap.originalDocumentIds,
        files: (data.files ?? []).filter((f): f is File => f instanceof File),
        runCommit
      })
      onSuccess?.()
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <EventFormShell
      formState={formState}
      title={`${bootstrap.editEventId != null ? 'Edit' : 'Add'} Operational`}
      isLoading={isLoading}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    >
      <OperationalForm traderId={traderId} />
    </EventFormShell>
  )
}
